import React from "react";
import { Link as RouterLink } from "react-router-dom";
import { Shield, ArrowLeft } from "lucide-react";
import { Button } from "../components/ui/button";
import SEO from "../components/SEO";

export default function Terminos() {
  return (
    <>
      <SEO 
        title="Términos y Condiciones | WPRescate24"
        description="Términos y condiciones de uso de los servicios de WPRescate24, agencia especializada en rescate y mantenimiento WordPress de GAREY AW."
        canonicalUrl="https://wprescate24.com/terminos"
      />
      <div className="dark min-h-screen bg-background">
        {/* Header Simple */}
        <header className="sticky top-0 z-50 backdrop-blur-sm bg-white/95 border-b border-border shadow-sm">
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
            <RouterLink to="/" className="flex items-center gap-2">
              <Shield className="w-6 h-6 text-primary" />
              <span className="font-bold text-foreground text-lg">WPRescate24</span>
            </RouterLink>
            <Button variant="outline" asChild>
              <RouterLink to="/" className="flex items-center gap-2">
                <ArrowLeft className="w-4 h-4" />
                Volver al inicio
              </RouterLink>
            </Button>
          </div>
        </header>

        {/* Contenido */}
        <main className="max-w-4xl mx-auto px-6 py-16">
          <h1 className="text-4xl font-bold text-foreground mb-8">Términos y Condiciones</h1>
          <p className="text-foreground/70 mb-8">
            <strong>Última actualización:</strong> {new Date().toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' })}
          </p>

          <div className="prose prose-lg max-w-none space-y-8">
            {/* 1. Aceptación */}
            <section>
              <h2 className="text-2xl font-bold text-foreground mb-4">1. Aceptación de los Términos</h2>
              <p className="text-foreground/80 leading-relaxed">
                Al acceder y utilizar los servicios de WPRescate24, una división de GAREY AW, usted acepta estar sujeto a estos Términos y Condiciones. Si no está de acuerdo con alguna parte de estos términos, no debe utilizar nuestros servicios.
              </p>
            </section>

            {/* 2. Servicios */}
            <section>
              <h2 className="text-2xl font-bold text-foreground mb-4">2. Descripción de los Servicios</h2>
              <p className="text-foreground/80 leading-relaxed mb-4">
                WPRescate24 ofrece los siguientes servicios profesionales:
              </p>
              <ul className="list-disc list-inside text-foreground/80 space-y-2 ml-4">
                <li>Rescate y recuperación de sitios WordPress hackeados o caídos</li>
                <li>Mantenimiento preventivo y correctivo de WordPress</li>
                <li>Optimización de velocidad y rendimiento</li>
                <li>Hosting WordPress administrado</li>
                <li>Auditorías de seguridad y blindaje</li>
              </ul>
            </section>

            {/* 3. Obligaciones del Cliente */}
            <section>
              <h2 className="text-2xl font-bold text-foreground mb-4">3. Obligaciones del Cliente</h2>
              <p className="text-foreground/80 leading-relaxed mb-4">
                Al contratar nuestros servicios, usted se compromete a:
              </p>
              <ul className="list-disc list-inside text-foreground/80 space-y-2 ml-4">
                <li>Proporcionar acceso completo y oportuno a su sitio web, hosting y panel de control</li>
                <li>Mantener copias de seguridad propias de su sitio web</li>
                <li>Proporcionar información veraz y completa sobre su sitio y su situación</li>
                <li>Pagar los servicios contratados según las condiciones acordadas</li>
                <li>No utilizar nuestros servicios para actividades ilegales o no autorizadas</li>
              </ul>
            </section>

            {/* 4. Garantías y Limitaciones */}
            <section>
              <h2 className="text-2xl font-bold text-foreground mb-4">4. Garantías y Limitaciones</h2>
              <p className="text-foreground/80 leading-relaxed mb-4">
                <strong>Garantías:</strong>
              </p>
              <ul className="list-disc list-inside text-foreground/80 space-y-2 ml-4 mb-4">
                <li>Garantizamos la recuperación de sitios en menos de 24 horas para planes Oro</li>
                <li>Si un problema persiste por la misma causa dentro de 7 días, lo resolveremos sin costo adicional</li>
                <li>Realizaremos copias de seguridad antes de cualquier intervención importante</li>
              </ul>
              <p className="text-foreground/80 leading-relaxed mb-4">
                <strong>Limitaciones:</strong>
              </p>
              <ul className="list-disc list-inside text-foreground/80 space-y-2 ml-4">
                <li>No somos responsables por pérdida de datos si no se proporcionaron backups previos</li>
                <li>No garantizamos la recuperación al 100% en casos de daño extremo o irreparable</li>
                <li>Los tiempos de respuesta pueden variar según la complejidad del caso</li>
                <li>No somos responsables por contenido ilegal o que infrinja derechos de terceros</li>
              </ul>
            </section>

            {/* 5. Pagos y Facturación */}
            <section>
              <h2 className="text-2xl font-bold text-foreground mb-4">5. Pagos y Facturación</h2>
              <ul className="list-disc list-inside text-foreground/80 space-y-2 ml-4">
                <li>Los pagos deben realizarse por adelantado salvo acuerdo contrario</li>
                <li>Los precios están expresados en dólares estadounidenses (USD)</li>
                <li>Los planes de mantenimiento se facturan mensualmente</li>
                <li>No se realizan reembolsos una vez iniciado el servicio</li>
                <li>Nos reservamos el derecho de modificar precios con previo aviso de 30 días</li>
              </ul>
            </section>

            {/* 6. Confidencialidad */}
            <section>
              <h2 className="text-2xl font-bold text-foreground mb-4">6. Confidencialidad</h2>
              <p className="text-foreground/80 leading-relaxed">
                Toda la información de acceso, datos del sitio y comunicaciones con el cliente son tratados de forma confidencial. No compartiremos su información con terceros sin su consentimiento expreso, excepto cuando sea requerido por ley.
              </p>
            </section>

            {/* 7. Propiedad Intelectual */}
            <section>
              <h2 className="text-2xl font-bold text-foreground mb-4">7. Propiedad Intelectual</h2>
              <p className="text-foreground/80 leading-relaxed">
                El cliente retiene todos los derechos sobre el contenido y diseño de su sitio web. WPRescate24 retiene los derechos sobre las herramientas, scripts y metodologías propias utilizadas para prestar los servicios. Cualquier documentación o reporte entregado es propiedad del cliente.
              </p>
            </section>

            {/* 8. Terminación */}
            <section>
              <h2 className="text-2xl font-bold text-foreground mb-4">8. Terminación del Servicio</h2>
              <p className="text-foreground/80 leading-relaxed mb-4">
                Cualquiera de las partes puede terminar el servicio bajo las siguientes condiciones:
              </p>
              <ul className="list-disc list-inside text-foreground/80 space-y-2 ml-4">
                <li>El cliente puede cancelar planes mensuales con 30 días de anticipación</li>
                <li>Nos reservamos el derecho de terminar el servicio si hay falta de pago</li>
                <li>Podemos suspender servicios si detectamos actividad ilegal o dañina</li>
                <li>En caso de terminación, entregaremos toda la información del cliente</li>
              </ul>
            </section>

            {/* 9. Limitación de Responsabilidad */}
            <section>
              <h2 className="text-2xl font-bold text-foreground mb-4">9. Limitación de Responsabilidad</h2>
              <p className="text-foreground/80 leading-relaxed">
                En ningún caso WPRescate24 o GAREY AW serán responsables por daños indirectos, incidentales, especiales o consecuentes que resulten del uso o la incapacidad de usar nuestros servicios. Nuestra responsabilidad total no excederá el monto pagado por el servicio contratado.
              </p>
            </section>

            {/* 10. Modificaciones */}
            <section>
              <h2 className="text-2xl font-bold text-foreground mb-4">10. Modificaciones a los Términos</h2>
              <p className="text-foreground/80 leading-relaxed">
                Nos reservamos el derecho de modificar estos términos en cualquier momento. Las modificaciones serán efectivas inmediatamente después de su publicación en nuestro sitio web. El uso continuado de nuestros servicios después de las modificaciones constituye la aceptación de los nuevos términos.
              </p>
            </section>

            {/* 11. Ley Aplicable */}
            <section>
              <h2 className="text-2xl font-bold text-foreground mb-4">11. Ley Aplicable y Jurisdicción</h2>
              <p className="text-foreground/80 leading-relaxed">
                Estos términos se rigen por las leyes de los Estados Unidos Mexicanos. Cualquier disputa será resuelta en los tribunales de Toluca, Estado de México.
              </p>
            </section>

            {/* 12. Contacto */}
            <section>
              <h2 className="text-2xl font-bold text-foreground mb-4">12. Información de Contacto</h2>
              <p className="text-foreground/80 leading-relaxed mb-4">
                Para preguntas sobre estos términos y condiciones, contáctanos:
              </p>
              <div className="bg-primary/5 border border-primary/20 rounded-lg p-6">
                <p className="text-foreground/80 mb-2"><strong>WPRescate24</strong></p>
                <p className="text-foreground/80 mb-2">Una empresa de GAREY AW</p>
                <p className="text-foreground/80 mb-2">Email: <a href="mailto:ventas@wprescate24.com" className="text-primary hover:underline">ventas@wprescate24.com</a></p>
                <p className="text-foreground/80 mb-2">WhatsApp: <a href="https://wa.me/527223818617" className="text-primary hover:underline">+52 722 381 8617</a></p>
                <p className="text-foreground/80">Web: <a href="https://wprescate24.com" className="text-primary hover:underline">wprescate24.com</a></p>
              </div>
            </section>
          </div>

          {/* Botón Volver */}
          <div className="mt-12 pt-8 border-t border-border text-center">
            <Button asChild className="bg-primary hover:bg-primary/90 text-white font-semibold px-8 py-6">
              <RouterLink to="/">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Volver al Inicio
              </RouterLink>
            </Button>
          </div>
        </main>

        {/* Footer Simple */}
        <footer className="border-t border-border bg-background py-8">
          <div className="max-w-7xl mx-auto px-6 text-center text-sm text-foreground/60">
            <p>© {new Date().getFullYear()} WPRescate24. Todos los derechos reservados. Una empresa de <a href="https://garey.mx" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline font-semibold">GAREY AW</a></p>
          </div>
        </footer>
      </div>
    </>
  );
}
